import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { Botao, Campo, Cartao, classesEntrada, TituloSecao } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import { useTurmasAdmin } from './comum'

interface Resultado {
  enviados: number
  falhas: number
  destinatarios: number
}

/**
 * Envio de aviso avulso (seção 14).
 *
 * O envio acontece na Edge Function `enviar-aviso`, não aqui: a chave
 * VAPID privada não pode existir no navegador. Esta tela só monta a
 * mensagem e chama.
 */
export function SecaoNotificacoes() {
  const [titulo, setTitulo] = useState('')
  const [corpo, setCorpo] = useState('')
  const [turmaId, setTurmaId] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [resultado, setResultado] = useState<Resultado | null>(null)

  const { data: todasAsTurmas = [] } = useTurmasAdmin()
  const turmas = todasAsTurmas.filter((t) => t.ativa)

  const enviar = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('enviar-aviso', {
        body: { titulo: titulo.trim(), corpo: corpo.trim(), turmaId },
      })
      if (error) throw error
      return data as Resultado
    },
    onSuccess: (dados) => {
      setResultado(dados)
      setErro(null)
      setTitulo('')
      setCorpo('')
    },
    onError: (e) => {
      setResultado(null)
      setErro(mensagemDeErro(e))
    },
  })

  const podeEnviar = titulo.trim().length > 0 && corpo.trim().length > 0

  return (
    <div className="flex flex-col gap-6">
      <section>
        <TituloSecao>Enviar um aviso</TituloSecao>
        <Cartao className="flex flex-col gap-4">
          <Campo rotulo="Título" dica="Aparece em negrito na notificação.">
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              maxLength={60}
              placeholder="Encontro ao vivo hoje"
              className={classesEntrada}
            />
          </Campo>

          <Campo
            rotulo="Mensagem"
            dica={`${corpo.length}/160 — textos longos são cortados pelo celular.`}
          >
            <textarea
              rows={3}
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              maxLength={160}
              placeholder="Às 20h, no grupo do WhatsApp. Não perca!"
              className="w-full resize-none rounded-2xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-4 text-base leading-relaxed"
            />
          </Campo>

          <fieldset>
            <legend className="mb-2 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
              Para quem
            </legend>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTurmaId(null)}
                aria-pressed={turmaId === null}
                className={cn(
                  'h-10 rounded-full px-4 text-sm font-medium transition-colors',
                  turmaId === null
                    ? 'bg-coral text-white'
                    : 'bg-[var(--fundo)] text-[var(--tinta-suave)]',
                )}
              >
                Todas as turmas
              </button>
              {turmas.map((turma) => (
                <button
                  key={turma.id}
                  type="button"
                  onClick={() => setTurmaId(turma.id)}
                  aria-pressed={turmaId === turma.id}
                  className={cn(
                    'h-10 rounded-full px-4 text-sm font-medium transition-colors',
                    turmaId === turma.id
                      ? 'bg-coral text-white'
                      : 'bg-[var(--fundo)] text-[var(--tinta-suave)]',
                  )}
                >
                  {turma.nome}
                </button>
              ))}
            </div>
          </fieldset>

          {/* Prévia: o envio é irreversível, então vale ver antes. */}
          {podeEnviar && (
            <div className="rounded-2xl border-2 border-dashed border-[var(--borda)] p-4">
              <p className="mb-2 text-xs font-medium text-[var(--tinta-apagada)]">
                Como vai aparecer no celular
              </p>
              <div className="flex gap-3 rounded-xl bg-[var(--fundo)] p-3">
                <span className="text-xl" aria-hidden>
                  🌺
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{titulo}</p>
                  <p className="text-sm text-[var(--tinta-suave)]">{corpo}</p>
                </div>
              </div>
            </div>
          )}

          {erro && (
            <p role="alert" className="rounded-2xl bg-pink/10 p-3 text-sm text-pink">
              {erro}
            </p>
          )}

          {resultado && (
            <div className="rounded-2xl bg-verde/10 p-3 text-sm">
              <p className="font-titulo font-semibold text-verde">
                Enviado para {resultado.enviados}{' '}
                {resultado.enviados === 1 ? 'aparelho' : 'aparelhos'}
              </p>
              <p className="mt-0.5 text-[var(--tinta-suave)]">
                {resultado.destinatarios}{' '}
                {resultado.destinatarios === 1 ? 'participante' : 'participantes'} na seleção
                {resultado.falhas > 0 && ` · ${resultado.falhas} falha(s)`}
              </p>
              {resultado.enviados === 0 && resultado.destinatarios > 0 && (
                <p className="mt-2 text-xs text-[var(--tinta-apagada)]">
                  Ninguém recebeu: as participantes precisam ter instalado o app e autorizado as
                  notificações.
                </p>
              )}
            </div>
          )}

          <Botao
            tamanho="bloco"
            disabled={!podeEnviar}
            carregando={enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            Enviar agora
          </Botao>

          <p className="text-center text-xs text-[var(--tinta-apagada)]">
            O envio é imediato e não pode ser desfeito.
          </p>
        </Cartao>
      </section>

      <HistoricoEnvios />
    </div>
  )
}

/**
 * Histórico de envios.
 *
 * A diferença entre "enviado" e "exibido" é a taxa real de entrega do
 * push no iOS — o dado que a primeira turma existe para produzir antes
 * de o programa escalar.
 */
function HistoricoEnvios() {
  const { data: envios = [] } = useQuery({
    queryKey: ['admin', 'notificacoes-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notificacoes_log')
        .select('id, titulo, enviado_em, exibido_em, clicado_em, erro')
        .order('enviado_em', { ascending: false })
        .limit(40)

      if (error) throw error
      return data as {
        id: string
        titulo: string
        enviado_em: string
        exibido_em: string | null
        clicado_em: string | null
        erro: string | null
      }[]
    },
  })

  if (envios.length === 0) return null

  const exibidos = envios.filter((e) => e.exibido_em).length
  const taxa = Math.round((exibidos / envios.length) * 100)

  return (
    <section>
      <TituloSecao
        acao={
          <span className="font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            {taxa}% entregues
          </span>
        }
      >
        Últimos envios
      </TituloSecao>

      <Cartao className="p-0">
        <ul>
          {envios.slice(0, 12).map((envio, indice) => (
            <li
              key={envio.id}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                indice > 0 && 'border-t border-[var(--borda)]',
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{envio.titulo}</span>
                <span className="block text-xs text-[var(--tinta-apagada)]">
                  {formatarData(envio.enviado_em, "d/MM 'às' HH:mm")}
                </span>
              </span>

              <span
                className={cn(
                  'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium',
                  envio.erro
                    ? 'bg-pink/15 text-pink'
                    : envio.clicado_em
                      ? 'bg-verde/15 text-verde'
                      : envio.exibido_em
                        ? 'bg-dourado/20 text-marrom'
                        : 'bg-[var(--borda)] text-[var(--tinta-apagada)]',
                )}
              >
                {envio.erro
                  ? 'falhou'
                  : envio.clicado_em
                    ? 'aberta'
                    : envio.exibido_em
                      ? 'entregue'
                      : 'enviada'}
              </span>
            </li>
          ))}
        </ul>
      </Cartao>

      <p className="mt-2 text-xs leading-relaxed text-[var(--tinta-apagada)]">
        <strong>enviada</strong> saiu do servidor · <strong>entregue</strong> apareceu no celular ·
        <strong> aberta</strong> foi tocada. A diferença entre as duas primeiras é a taxa real de
        entrega no iOS.
      </p>
    </section>
  )
}
